// @ts-nocheck
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Mail, Shield, User, AlertCircle, Eye, EyeOff, Check } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { getInitials, getAvatarColor } from '@/lib/utils'
import { api } from '@/lib/api'

const setPasswordSchema = z.object({
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
})

type SetPasswordFormData = z.infer<typeof setPasswordSchema>

interface InvitationData {
  email: string
  organization: {
    id: string
    name: string
    avatar?: string
  }
  inviter: {
    firstName: string
    lastName: string
  }
  invitedUser?: {
    firstName?: string | null
    lastName?: string | null
  }
  role: string
  userExists: boolean
}

interface InvitationResponse {
  data?: InvitationData
  error?: string
}

interface AuthResponse {
  redirect?: string
  data?: {
    requiresPasswordSetup?: boolean
  }
  error?: string
}

export function InvitePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [invitation, setInvitation] = useState<InvitationData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const form = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Please check your email for the correct link.')
      setLoading(false)
      return
    }

    validateInvitation(token)
  }, [token])

  const validateInvitation = async (invitationToken: string) => {
    try {
      const result = await api.post<InvitationResponse>('/auth/validate-invitation', { token: invitationToken })
      if (!result?.data) {
        throw new Error(result?.error || 'Failed to validate invitation')
      }

      setInvitation(result.data)
    } catch (err) {
      console.error('Failed to validate invitation', err)
      setError(err instanceof Error ? err.message : 'Failed to validate invitation')
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (formData: SetPasswordFormData) => {
    if (!token || !invitation) return

    if (!invitation.userExists) {
      if (!formData.password || formData.password.length === 0) {
        setError('Password is required for new accounts')
        return
      }
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters')
        return
      }
      if (!/(?=.*[a-z])/.test(formData.password)) {
        setError('Password must contain at least one lowercase letter')
        return
      }
      if (!/(?=.*[A-Z])/.test(formData.password)) {
        setError('Password must contain at least one uppercase letter')
        return
      }
      if (!/(?=.*\d)/.test(formData.password)) {
        setError('Password must contain at least one number')
        return
      }
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords don't match")
        return
      }
    }

    setSubmitting(true)
    setError(null)

    try {
      if (!invitation.userExists) {
        const derivedFirstName = invitation.invitedUser?.firstName?.trim() || invitation.email.split('@')[0]
        const derivedLastName = invitation.invitedUser?.lastName?.trim() || 'Member'

        const registerResult = await api.post<AuthResponse>('/auth/register', {
          email: invitation.email,
          password: formData.password,
          firstName: derivedFirstName,
          lastName: derivedLastName,
          invitationToken: token,
        })

        if (registerResult?.error) {
          throw new Error(registerResult.error || 'Failed to create account')
        }

        navigate('/dashboard')
        return
      }

      const loginResult = await api.post<AuthResponse>('/auth/invitation-login', { token })

      if (loginResult?.error) {
        throw new Error(loginResult.error || 'Failed to accept invitation')
      }

      const redirectUrl = loginResult.redirect || '/dashboard'
      navigate(redirectUrl)
    } catch (err) {
      console.error('Invitation acceptance failed', err)
      if (err instanceof Error) {
        let friendlyMessage = err.message
        try {
          const parsed = JSON.parse(err.message)
          if (parsed?.details?.fieldErrors) {
            const firstFieldError = Object.values(parsed.details.fieldErrors).flat()[0]
            if (typeof firstFieldError === 'string' && firstFieldError.length > 0) {
              friendlyMessage = firstFieldError
            }
          } else if (parsed?.error) {
            friendlyMessage = parsed.error
          }
        } catch (parseError) {
          // Ignore JSON parse errors, fallback to original message
        }

        setError(friendlyMessage)
      } else {
        setError('An error occurred while accepting the invitation. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-muted to-accent">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-muted to-accent">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl">Invitation Invalid</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!invitation) {
    return null
  }

  const inviteeName = [invitation.invitedUser?.firstName, invitation.invitedUser?.lastName]
    .filter((value) => Boolean(value && value.trim().length > 0))
    .join(' ') || invitation.email.split('@')[0]

  const inviterName = [invitation.inviter.firstName, invitation.inviter.lastName]
    .filter((value) => Boolean(value && value.trim().length > 0))
    .join(' ') || 'A team member'

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl shadow-xl border border-border">
        <CardContent className="p-8 grid gap-8 lg:grid-cols-[360px_1fr]">
          <div className="bg-muted/40 rounded-2xl border border-border p-6 space-y-6">
            <div className="flex items-center gap-4">
              <Avatar className="h-14 w-14" style={{ backgroundColor: getAvatarColor(invitation.organization.name) }}>
                {invitation.organization.avatar ? (
                  <AvatarImage src={invitation.organization.avatar} alt={invitation.organization.name} />
                ) : (
                  <AvatarFallback className="text-lg font-semibold text-primary-foreground">
                    {getInitials(invitation.organization.name)}
                  </AvatarFallback>
                )}
              </Avatar>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Join {invitation.organization.name}
                </h2>
                <p className="text-sm text-muted-foreground">Invitation for {inviteeName}</p>
                <Badge variant="outline" className="mt-1">
                  {invitation.role}
                </Badge>
              </div>
            </div>

            <Separator />

            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-foreground font-medium">Invitation sent to</p>
                  <p>{invitation.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-foreground font-medium">Invited by</p>
                  <p>{inviterName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-foreground font-medium">Access level</p>
                  <p>{invitation.role}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Accept Invitation</h1>
              <p className="text-muted-foreground">
                {invitation.userExists
                  ? 'We found an account associated with this email. Sign in to accept the invitation.'
                  : 'Create your password to join the organization securely.'}
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {!invitation.userExists ? (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Create Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              placeholder="Enter a strong password"
                              type={showPassword ? 'text' : 'password'}
                              disabled={submitting}
                              {...field}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((prev) => !prev)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              placeholder="Re-enter your password"
                              type={showConfirmPassword ? 'text' : 'password'}
                              disabled={submitting}
                              {...field}
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword((prev) => !prev)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="bg-muted/40 border border-border rounded-xl p-4 text-sm text-muted-foreground space-y-2">
                    <p className="font-medium text-foreground">Password requirements:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>At least 8 characters</li>
                      <li>One uppercase and one lowercase letter</li>
                      <li>At least one number</li>
                    </ul>
                  </div>

                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? 'Creating account...' : 'Create account & join'}
                  </Button>
                </form>
              </Form>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-muted/40 border border-border rounded-xl text-sm text-muted-foreground">
                  <p>
                    We found an existing account associated with{' '}
                    <span className="font-medium text-foreground">{invitation.email}</span>.
                    Click the button below to sign in and join the organization.
                  </p>
                </div>
                <Button className="w-full" onClick={() => onSubmit({})} disabled={submitting}>
                  {submitting ? 'Signing in...' : 'Accept Invitation'}
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="w-4 h-4 text-success" />
              <span>Secure invitation — powered by Gathio</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default InvitePage
